import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ChessGame } from "@/components/ChessGame";
import { SettingsModal } from "@/components/SettingsModal";
import { GameSelector } from "@/components/GameSelector";
import { PgnImportModal } from "@/components/PgnImportModal";
import { Settings, Plus, Upload } from "lucide-react";
import { detectApiClient } from "@/lib/apiClient";

// localStorage keys
const STORAGE_KEYS = {
  CURRENT_GAME_ID: 'chess_current_game_id',
};

const Home = () => {
  const [currentGame, setCurrentGame] = useState(null);
  const [games, setGames] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showNewGame, setShowNewGame] = useState(false);
  const [showImportPgn, setShowImportPgn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiClient, setApiClient] = useState(null);

  // Load saved game ID from localStorage
  const loadSavedGame = useCallback(async () => {
    if (!apiClient) return null;
    const savedGameId = localStorage.getItem(STORAGE_KEYS.CURRENT_GAME_ID);
    if (savedGameId) {
      try {
        return await apiClient.getGame(savedGameId);
      } catch (e) {
        // Game not found, clear localStorage
        localStorage.removeItem(STORAGE_KEYS.CURRENT_GAME_ID);
      }
    }
    return null;
  }, [apiClient]);

  const fetchGames = useCallback(async () => {
    try {
      if (!apiClient) return [];
      const allGames = await apiClient.getGames();
      setGames(allGames);
      return allGames;
    } catch (e) {
      console.error("Error fetching games:", e);
      return [];
    }
  }, [apiClient]);

  const fetchSettings = useCallback(async () => {
    try {
      if (!apiClient) return null;
      const saved = await apiClient.getSettings();
      setSettings(saved);
      return saved;
    } catch (e) {
      console.error("Error fetching settings:", e);
      return null;
    }
  }, [apiClient]);

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      const client = await detectApiClient();
      setApiClient(client);
      setLoading(false);
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (!apiClient) return;
    const init = async () => {
      setLoading(true);
      
      // Load settings first
      await fetchSettings();
      
      // Try to load saved game
      const savedGame = await loadSavedGame();
      if (savedGame) {
        setCurrentGame(savedGame);
      }
      
      // Fetch all games
      const allGames = await fetchGames();
      
      // If no saved game, auto-select most recent active game
      if (!savedGame && allGames.length > 0) {
        const activeGame = allGames.find(g => g.status === 'active');
        if (activeGame) {
          setCurrentGame(activeGame);
          localStorage.setItem(STORAGE_KEYS.CURRENT_GAME_ID, activeGame.id);
        }
      }
      
      setLoading(false);
    };
    init();
  }, [apiClient, fetchGames, fetchSettings, loadSavedGame]);

  const handleCreateGame = async (playerColor, opponentName) => {
    try {
      if (!apiClient) return;
      const previousGameId = currentGame?.id || null;
      const createdGame = await apiClient.createGame({
        player_color: playerColor,
        opponent_name: opponentName || "Roommate"
      });

      if (previousGameId && previousGameId !== createdGame.id) {
        try {
          await apiClient.deleteGame(previousGameId);
        } catch (cleanupError) {
          console.warn("Failed to purge previous game during new game creation:", cleanupError);
        }
      }

      setCurrentGame(createdGame);
      setGames(prev => [
        createdGame,
        ...prev.filter(g => g.id !== createdGame.id && g.id !== previousGameId),
      ]);
      setShowNewGame(false);
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEYS.CURRENT_GAME_ID, createdGame.id);
    } catch (e) {
      console.error("Error creating game:", e);
    }
  };

  const handleSelectGame = (game) => {
    setCurrentGame(game);
    localStorage.setItem(STORAGE_KEYS.CURRENT_GAME_ID, game.id);
  };

  const handleGameUpdate = (updatedGame) => {
    setCurrentGame(updatedGame);
    setGames(prev => {
      const exists = prev.some(g => g.id === updatedGame.id);
      return exists
        ? prev.map(g => g.id === updatedGame.id ? updatedGame : g)
        : [updatedGame, ...prev];
    });
    localStorage.setItem(STORAGE_KEYS.CURRENT_GAME_ID, updatedGame.id);
  };

  const handleSettingsUpdate = async (newSettings) => {
    try {
      if (!apiClient) return;
      const updated = await apiClient.updateSettings(newSettings);
      setSettings(updated);
      setShowSettings(false);
    } catch (e) {
      console.error("Error updating settings:", e);
    }
  };

  const handleImportPgn = async (payload) => {
    try {
      if (!apiClient) return;
      const importedGame = await apiClient.importPgn(payload);

      setCurrentGame(importedGame);
      setGames(prev => [importedGame, ...prev.filter(g => g.id !== importedGame.id)]);
      localStorage.setItem(STORAGE_KEYS.CURRENT_GAME_ID, importedGame.id);
      setShowImportPgn(false);
    } catch (e) {
      console.error("Error importing PGN:", e);
      throw e;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-heading text-white/60 text-sm tracking-widest uppercase">Loading</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] noise-overlay" data-testid="chess-companion-app">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <h1 className="font-heading text-lg sm:text-xl font-semibold tracking-tight text-white truncate">
              Grandmaster's Terminal
            </h1>
            {currentGame && (
              <span className="hidden sm:inline-flex text-xs font-mono text-white/40 bg-white/5 px-3 py-1 rounded shrink-0">
                vs {currentGame.opponent_name}
              </span>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              onClick={() => setShowImportPgn(true)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-white/10 text-white/70 font-medium text-[11px] sm:text-xs uppercase tracking-wide rounded-sm transition-colors hover:bg-white/5"
              data-testid="import-pgn-btn"
            >
              <Upload size={14} />
              <span>Import PGN</span>
            </button>
            <button
              onClick={() => setShowNewGame(true)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#D4AF37] text-black font-medium text-[11px] sm:text-xs uppercase tracking-wide rounded-sm btn-gold-glow transition-colors hover:bg-[#E5C158]"
              data-testid="new-game-btn"
            >
              <Plus size={14} />
              <span>New Game</span>
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 rounded-sm border border-white/10 hover:bg-white/5 transition-colors"
              data-testid="settings-btn"
            >
              <Settings size={18} className="text-white/60" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 min-h-screen">
        {currentGame ? (
          <ChessGame
            game={currentGame}
            settings={settings}
            onGameUpdate={handleGameUpdate}
            apiClient={apiClient}
          />
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[80vh] px-6">
            <div className="text-center max-w-md">
              <h2 className="font-heading text-3xl font-light text-white mb-4">
                No Active Game
              </h2>
              <p className="text-white/50 mb-8">
                Start a new game to begin your chess session with engine-backed move guidance.
              </p>
              <button
                onClick={() => setShowNewGame(true)}
                className="px-8 py-3 bg-[#D4AF37] text-black font-medium text-sm uppercase tracking-wide rounded-sm btn-gold-glow transition-colors hover:bg-[#E5C158]"
                data-testid="start-new-game-btn"
              >
                Start New Game
              </button>
            </div>
            
            {games.length > 0 && (
              <div className="mt-12 w-full max-w-2xl">
                <h3 className="font-heading text-sm font-medium text-white/40 uppercase tracking-widest mb-4">
                  Recent Games
                </h3>
                <div className="space-y-2">
                  {games.slice(0, 5).map(game => (
                    <button
                      key={game.id}
                      onClick={() => handleSelectGame(game)}
                      className="w-full p-4 bg-[#0A0A0A] border border-white/5 rounded-md hover:border-white/10 transition-colors text-left"
                      data-testid={`game-item-${game.id}`}
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <span className="text-white font-medium">vs {game.opponent_name}</span>
                          <span className="block sm:inline text-white/40 text-sm sm:ml-3">
                            Playing as {game.player_color}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <span className="font-mono text-xs text-white/40">
                            {game.moves.length} moves
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            game.status === 'active' ? 'bg-green-900/30 text-green-400' :
                            game.status === 'checkmate' ? 'bg-red-900/30 text-red-400' :
                            'bg-white/10 text-white/60'
                          }`}>
                            {game.status}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Modals */}
      <GameSelector
        open={showNewGame}
        onClose={() => setShowNewGame(false)}
        onCreateGame={handleCreateGame}
      />

      <PgnImportModal
        open={showImportPgn}
        onClose={setShowImportPgn}
        onImport={handleImportPgn}
      />
      
      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        onSave={handleSettingsUpdate}
      />
      
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
